import { Component,
  EventEmitter,
  Input,
  OnInit,
  OnDestroy,
  Output,
  ChangeDetectionStrategy,
  ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ConfirmationModalConfig } from '../../../shared/components/confirmation-modal/confirmation-modal.component';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { SchPSaveService } from './sch-p-save.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams
} from '../../../shared/workspace-element-context';

import { safeJsonInput } from '../../../shared/_utils/safe-json-input';
@Component({
    selector: 'gtw-sch-p-save',
    templateUrl: './sch-p-save.component.html',
    styleUrls: ['./sch-p-save.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class SchPSaveComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  private readonly subs = new Subscription();
  private isInitialized = false;
  crudLoading = false;
  showModal = false;

  headerData: any = {};
  globalParams: any = {};
  filterParams: any = {};
  rowData: any = {};
  gridData: any[] = [];
  GENERAL_CONFIG: any = {};
  USER_SETTINGS: any = {};
  logged_in_user = '';
  modalConfig: ConfirmationModalConfig = {
    title: 'Confirm',
    message: 'Are you sure you want to save the Insurance Schedule P Data?',
    iconClass: 'fa fa-sign-out',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true,
    animationClass: 'animated fadeIn fast',
    loading: false,
    loadingText: 'Processing...'
  };

  @Input('grid-data')
  set _grid_data(gridData: string) {
    try {
      const data = safeJsonInput<any>(gridData || '[]', null, 'sch-p-save.parse1');
      this.gridData = Array.isArray(data) ? data : [];
      this.tryInit();
    } catch (e) {
      this.gridData = [];
    }
  }

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private cdr: ChangeDetectorRef,
    private gtwSnackbarService: GtwSnackbarService,
    private schPSaveService: SchPSaveService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => { this.applyWorkspaceContext(); this.tryInit(); }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => { this.applyWorkspaceContext(); this.tryInit(); }));
    this.tryInit();
  }

  ngOnDestroy(): void { this.subs.unsubscribe(); }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.headerData = header;
    this.globalParams = header.globalParams || {};
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.USER_SETTINGS = header.userSettingsData || {};
    this.logged_in_user = this.USER_SETTINGS?.user?.sso_id ?? '';
    this.filterParams = normalizeFilterParams(activeScreen);
    this.cdr.detectChanges();
  }

  private tryInit(): void {
    if (this.isInitialized) return;
    const hasConfig = this.GENERAL_CONFIG?.base_url;
    const hasFilterParams = this.filterParams && Object.keys(this.filterParams).length > 0;
    if (!hasConfig || !hasFilterParams) return;
    this.isInitialized = true;
    this.showModal = true;
    this.cdr.detectChanges();
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }

  confirm_save(): void {
    const fp = this.filterParams;
    if (fp?.CDR === '0' || fp?.LEID === '0' || fp?.bsla === '0' || fp?.led_curr === '0' || fp?.reporting_p === '0') {
      this.gtwSnackbarService.openSnackBar('Please Select all the entity parameters to Save', 'gtw-snackbar--red');
      return;
    }

    const gridRows: any[] = [];
    if (Array.isArray(this.gridData)) {
      this.gridData.forEach((el: any) => {
        if (el?.data) {
          gridRows.push(el.data);
        }
      });
    }

    const returnClobSettingsObj: Record<string, any> = {
      sso_id: this.logged_in_user,
      tax_year: fp.tax_year,
      CDR: fp.CDR,
      LEID: fp.LEID,
      bsla: fp.bsla,
      led_curr: fp.led_curr,
      reporting_p: fp.reporting_p
    };

    const dataToSend = gridRows.map((data: any) => ({
      SECTION: data.SECTION,
      ACCT_TYPE: data.ACCT_TYPE,
      object_id: data.object_id,
      CHART_TYPE: data.CHART_TYPE,
      TAX_YEAR: data.TAX_YEAR,
      COL_25: data.COL_25,
      COL_26: data.COL_26,
      COL_35: data.COL_35,
      COL_34: data.COL_34,
      COL_2: data.COL_2
    }));

    this.crudLoading = true;
    this.modalConfig = { ...this.modalConfig, loading: true };
    this.cdr.detectChanges();

    this.schPSaveService.saveDetails(
      this.GENERAL_CONFIG.base_url,
      returnClobSettingsObj,
      dataToSend,
      this.globalParams,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (result: any) => {
        this.crudLoading = false;
        this.modalConfig = { ...this.modalConfig, loading: false };
        if (parseInt(result?.callSuccess, 10) === 1) {
          this.gtwSnackbarService.openSnackBar('Schedule P Data Submitted for Save!', 'gtw-snackbar--green');
          this.requestGridUpdate.emit('refresh');
        } else {
          this.gtwSnackbarService.openSnackBar(
            result?.errorMessage || 'An error occurred while saving',
            'gtw-snackbar--red'
          );
        }
        this.cdr.detectChanges();
      },
      () => {
        this.crudLoading = false;
        this.modalConfig = { ...this.modalConfig, loading: false };
        this.gtwSnackbarService.openSnackBar('Error occurred while saving', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      }
    );
  }
}
